
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { StorageLocation } from "./inventoryTypes";
import { useStorageLocationOperations } from "./inventoryService";

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
});

type FormValues = z.infer<typeof formSchema>;

interface StorageLocationDialogProps {
  location: StorageLocation | null;
  open: boolean;
  onClose: (updated: boolean) => void;
}

export function StorageLocationDialog({
  location,
  open,
  onClose,
}: StorageLocationDialogProps) {
  const { handleLocationCreate, handleLocationUpdate } = useStorageLocationOperations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: location?.name || "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    let success = false;

    if (location) {
      success = await handleLocationUpdate(location.id, data.name);
    } else {
      success = await handleLocationCreate(data.name);
    }

    setIsSubmitting(false);
    if (success) {
      onClose(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {location ? "Edit Storage Location" : "Add Storage Location"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter location name"
                      autoFocus
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onClose(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : location ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
